// Phase A — one-time onboarding college prompt.
//
// Shown on the dashboard when we still don't know a student's college (no
// outreach-link attribution). A typeahead over the canonical directory so every
// pick maps to one college (keeps the B2B rollup clean); "not listed" records a
// suggestion for admin review. Skippable — re-prompts next visit if skipped.
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Search, Loader2, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { authFetch } from '@/services/projectService';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const MAX_RESULTS = 30;

// Rank a college against the (lowercased) query: prefix hits beat word-start
// hits beat plain substring hits; name matches beat code/city matches. Lower
// score = better. Returns null when nothing matches.
const rank = (c, term) => {
  const name = (c.name || '').toLowerCase();
  const code = (c.short_code || '').toLowerCase();
  const city = (c.city || '').toLowerCase();
  const wordStart = (s) => s.split(/[\s.,()-]+/).some((w) => w.startsWith(term));

  if (name.startsWith(term)) return 0;
  if (code && code.startsWith(term)) return 1;
  if (wordStart(name)) return 2;
  if (name.includes(term)) return 3;
  if (city && (city.startsWith(term) || wordStart(city))) return 4;
  if (city && city.includes(term)) return 5;
  if (code && code.includes(term)) return 6;
  return null;
};

const CollegePrompt = ({ onDone }) => {
  const [q, setQ] = useState('');
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [otherMode, setOtherMode] = useState(false);
  const [other, setOther] = useState('');

  // Load the full active directory once, then filter locally — the list is
  // small enough that in-memory filtering is instant and avoids per-keystroke
  // network jank.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await authFetch(`${API_URL}/api/colleges?limit=1000`);
        const data = await res.json();
        if (alive) setAll(data.colleges || []);
      } catch {
        if (alive) setAll([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const results = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return all.slice(0, MAX_RESULTS);
    return all
      .map((c) => ({ c, s: rank(c, term) }))
      .filter((x) => x.s !== null)
      .sort((a, b) => a.s - b.s || (a.c.name || '').localeCompare(b.c.name || ''))
      .slice(0, MAX_RESULTS)
      .map((x) => x.c);
  }, [q, all]);

  const save = async (body) => {
    setSaving(true);
    try {
      const res = await authFetch(`${API_URL}/api/users/college`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`save failed: ${res.status}`);
      toast.success('Thanks — saved your college.');
      onDone?.();
    } catch (e) {
      toast.error("Couldn't save that. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-slate-100 p-6">
        <div className="flex items-start justify-between mb-1">
          <h2 className="text-lg font-bold text-slate-900">Which college are you from?</h2>
          <button
            onClick={() => onDone?.()}
            aria-label="Skip"
            className="p-1.5 -mr-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-50"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="text-sm text-slate-500 mb-4">
          Helps us tailor your experience and support your campus.
        </p>

        {!otherMode ? (
          <>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                autoFocus
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search your college…"
                className="pl-9 h-11"
              />
            </div>

            {/* Fixed height so the modal never grows/shrinks as matches change. */}
            <div className="mt-2 h-64 overflow-y-auto rounded-lg border border-slate-100 divide-y divide-slate-50">
              {loading ? (
                <div className="h-full flex items-center justify-center gap-2 text-slate-400 text-sm">
                  <Loader2 className="h-4 w-4 animate-spin" /> Loading colleges…
                </div>
              ) : results.length === 0 ? (
                <div className="h-full flex items-center justify-center text-sm text-slate-400">No matches.</div>
              ) : (
                results.map((c) => (
                  <button
                    key={c.slug}
                    disabled={saving}
                    onClick={() => save({ slug: c.slug })}
                    className="w-full text-left px-3 py-2.5 hover:bg-slate-50 disabled:opacity-50"
                  >
                    <div className="text-sm font-medium text-slate-800">{c.name}</div>
                    {c.city ? <div className="text-xs text-slate-400">{c.city}</div> : null}
                  </button>
                ))
              )}
            </div>

            <button
              onClick={() => setOtherMode(true)}
              className="mt-3 text-sm text-blue-600 hover:text-blue-700"
            >
              My college isn't listed
            </button>
          </>
        ) : (
          <>
            <Input
              autoFocus
              value={other}
              onChange={(e) => setOther(e.target.value)}
              placeholder="Type your college name"
              className="h-11"
            />
            <div className="flex gap-2 mt-3">
              <Button
                onClick={() => other.trim() && save({ other: other.trim() })}
                disabled={saving || !other.trim()}
                className="flex-1 h-11 bg-blue-600 hover:bg-blue-700 text-white"
              >
                {saving ? 'Saving…' : 'Submit'}
              </Button>
              <Button
                variant="outline"
                onClick={() => setOtherMode(false)}
                className="h-11"
              >
                Back
              </Button>
            </div>
          </>
        )}

        <button
          onClick={() => onDone?.()}
          className="mt-4 w-full text-center text-xs text-slate-400 hover:text-slate-600"
        >
          Skip for now
        </button>
      </div>
    </div>
  );
};

export default CollegePrompt;
